import type { Rng } from './rng';
import { createRng, defaultRng } from './rng';
import type { SampleInvoice, MlsStatus } from './invoices';

/**
 * Per-invoice operational artefacts — timeline events, TDD submission
 * lifecycle, archive record (with chained hashes), audit log entries.
 *
 * Everything is derived deterministically from the invoice itself
 * (`invoiceId`-seeded RNG). Refresh-stable.
 */

export type TimelineEventKind =
  | 'intake'
  | 'validate.syntax'
  | 'validate.business'
  | 'validate.trn'
  | 'validate.schematron'
  | 'sign'
  | 'as4.send'
  | 'mls.update'
  | 'tdd.submit'
  | 'archive';

export interface TimelineEvent {
  kind: TimelineEventKind;
  status: 'ok' | 'failed' | 'pending';
  at: string;
  detail?: string;
  /** `errorCode` only present when status === 'failed'. */
  errorCode?: string;
  durationMs?: number;
}

export interface InvoiceTimeline {
  invoiceId: string;
  events: TimelineEvent[];
  /** Convenience: most-recent event status. */
  currentStatus: MlsStatus;
}

export interface TddSubmission {
  invoiceId: string;
  tddId: string;
  status: 'PENDING' | 'SUBMITTED' | 'ACCEPTED' | 'FAILED' | 'DEAD_LETTER';
  retryCount: number;
  submittedAt: string | null;
  acceptedAt: string | null;
  emaraConfirmationId: string | null;
  deadline: string;
  taxBaseAed: number;
  vatAmountAed: number;
  errorMessage?: string;
}

export interface ArchiveRecord {
  archiveId: string;
  invoiceId: string;
  invoiceXmlHash: string;
  tddXmlHash: string;
  chainHash: string;
  storagePathXml: string;
  storagePathTdd: string;
  retentionUntil: string;
  archivedAt: string;
  legalHold: boolean;
}

function pseudoUuid(rng: Rng): string {
  const hex = (n: number) => rng.int(0, 15).toString(16).repeat(n);
  return `${hex(8)}-${hex(4)}-4${hex(3)}-a${hex(3)}-${hex(12)}`;
}

function fakeSha256(rng: Rng): string {
  let s = '';
  for (let i = 0; i < 64; i++) s += rng.int(0, 15).toString(16);
  return s;
}

/** Deterministic 32-bit seed from a UUID-shaped string. */
function seedFromId(id: string): number {
  let s = 0x811c9dc5; // FNV-1a-ish
  for (let i = 0; i < id.length; i++) {
    s ^= id.charCodeAt(i);
    s = Math.imul(s, 16777619) >>> 0;
  }
  return s || 1;
}

function plus(date: Date, ms: number): Date {
  return new Date(date.getTime() + ms);
}

export function generateInvoiceTimeline(invoice: SampleInvoice): InvoiceTimeline {
  const rng = createRng(seedFromId(invoice.invoiceId));
  const events: TimelineEvent[] = [];
  const start = new Date(`${invoice.issueDate}T08:00:00Z`);

  // intake
  let cursor = start;
  events.push({
    kind: 'intake',
    status: 'ok',
    at: cursor.toISOString(),
    detail: `Received via ${invoice.direction === 'OUTBOUND' ? 'API' : 'Peppol C3 inbound'}`,
    durationMs: rng.int(20, 80),
  });

  // validation 4 levels
  for (const level of ['validate.syntax', 'validate.business', 'validate.trn', 'validate.schematron'] as const) {
    cursor = plus(cursor, rng.int(80, 350));
    if (invoice.mlsStatus === 'RE' && level === 'validate.schematron' && rng.chance(0.5)) {
      events.push({
        kind: level,
        status: 'failed',
        at: cursor.toISOString(),
        detail: 'Schematron rejected: BR-AE-VAT-08 — VAT category mismatch',
        errorCode: 'BR-AE-VAT-08',
        durationMs: rng.int(220, 480),
      });
      events.push({
        kind: 'mls.update',
        status: 'ok',
        at: plus(cursor, 700).toISOString(),
        detail: 'Status: RE (Rejected)',
      });
      return { invoiceId: invoice.invoiceId, events, currentStatus: 'RE' };
    }
    events.push({
      kind: level,
      status: 'ok',
      at: cursor.toISOString(),
      durationMs: rng.int(40, 320),
    });
  }

  // sign
  cursor = plus(cursor, rng.int(60, 300));
  events.push({
    kind: 'sign',
    status: 'ok',
    at: cursor.toISOString(),
    detail: 'CloudHSM RSA-2048 + RFC 3161 timestamp',
    durationMs: rng.int(120, 280),
  });

  // AS4 send
  cursor = plus(cursor, rng.int(800, 4500));
  events.push({
    kind: 'as4.send',
    status: 'ok',
    at: cursor.toISOString(),
    detail: 'Peppol AS4 → recipient ASP',
    durationMs: rng.int(1200, 4500),
  });

  // MLS state
  cursor = plus(cursor, rng.int(2000, 30000));
  if (invoice.mlsStatus === 'RE') {
    events.push({
      kind: 'mls.update',
      status: 'failed',
      at: cursor.toISOString(),
      detail: 'Status: RE — recipient ASP rejected',
      errorCode: 'AE-AB-13',
    });
    return { invoiceId: invoice.invoiceId, events, currentStatus: 'RE' };
  }
  events.push({
    kind: 'mls.update',
    status: 'ok',
    at: cursor.toISOString(),
    detail: invoice.mlsStatus === 'AB' ? 'Status: AB (Accepted)' : 'Status: AB → AP',
  });

  if (invoice.mlsStatus === 'PENDING') {
    return { invoiceId: invoice.invoiceId, events, currentStatus: 'PENDING' };
  }

  // TDD submit
  cursor = plus(cursor, rng.int(2_000, 60_000));
  events.push({
    kind: 'tdd.submit',
    status: 'ok',
    at: cursor.toISOString(),
    detail: 'EmaraTax TDD accepted',
    durationMs: rng.int(800, 2200),
  });

  if (invoice.mlsStatus === 'AB') {
    return { invoiceId: invoice.invoiceId, events, currentStatus: 'AB' };
  }

  // archive (only AP)
  cursor = plus(cursor, rng.int(60_000, 300_000));
  events.push({
    kind: 'archive',
    status: 'ok',
    at: cursor.toISOString(),
    detail: 'WORM archive (chain-hashed, S3 Object Lock COMPLIANCE)',
    durationMs: rng.int(400, 1200),
  });

  return { invoiceId: invoice.invoiceId, events, currentStatus: 'AP' };
}

export function generateTddSubmissions(invoices: SampleInvoice[]): TddSubmission[] {
  return invoices.map((inv) => {
    const rng = createRng(seedFromId(inv.invoiceId) ^ 0x7d_d_5);
    const status: TddSubmission['status'] =
      inv.mlsStatus === 'AP'
        ? 'ACCEPTED'
        : inv.mlsStatus === 'AB'
        ? 'SUBMITTED'
        : inv.mlsStatus === 'RE'
        ? rng.chance(0.3)
          ? 'FAILED'
          : 'PENDING'
        : 'PENDING';
    const issuedAt = new Date(`${inv.issueDate}T08:00:00Z`);
    const deadline = new Date(issuedAt.getTime() + 14 * 86400_000);
    return {
      invoiceId: inv.invoiceId,
      tddId: pseudoUuid(rng),
      status,
      retryCount: status === 'FAILED' ? rng.int(1, 3) : 0,
      submittedAt:
        status === 'ACCEPTED' || status === 'SUBMITTED'
          ? new Date(issuedAt.getTime() + rng.int(60_000, 300_000)).toISOString()
          : null,
      acceptedAt:
        status === 'ACCEPTED'
          ? new Date(issuedAt.getTime() + rng.int(120_000, 600_000)).toISOString()
          : null,
      emaraConfirmationId:
        status === 'ACCEPTED'
          ? `EMTX-${String(rng.int(1, 999_999)).padStart(6, '0')}-${rng.int(1000, 9999)}`
          : null,
      deadline: deadline.toISOString(),
      taxBaseAed: inv.totalNetAmount,
      vatAmountAed: inv.totalVatAmount,
      errorMessage:
        status === 'FAILED' ? 'EmaraTax 503: upstream timeout (retry queued)' : undefined,
    };
  });
}

export function generateArchiveRecords(invoices: SampleInvoice[]): ArchiveRecord[] {
  let prevHash = '0'.repeat(64);
  return invoices
    .filter((inv) => inv.mlsStatus === 'AP')
    .map((inv) => {
      const rng = createRng(seedFromId(inv.invoiceId) ^ 0xa1c1_1ef);
      const xmlHash = fakeSha256(rng);
      const tddHash = fakeSha256(rng);
      const chain = fakeSha256(rng); // really would be SHA256(prev+xmlHash+tddHash)
      const archivedAt = new Date(`${inv.issueDate}T12:00:00Z`).toISOString();
      const retention = new Date(`${inv.issueDate}T12:00:00Z`);
      retention.setUTCFullYear(retention.getUTCFullYear() + 7);
      const rec: ArchiveRecord = {
        archiveId: pseudoUuid(rng),
        invoiceId: inv.invoiceId,
        invoiceXmlHash: xmlHash,
        tddXmlHash: tddHash,
        chainHash: chain,
        storagePathXml: `s3://solara-worm/${inv.invoiceId}/invoice.xml`,
        storagePathTdd: `s3://solara-worm/${inv.invoiceId}/tdd.xml`,
        retentionUntil: retention.toISOString().slice(0, 10),
        archivedAt,
        legalHold: rng.chance(0.04),
      };
      prevHash = chain;
      return rec;
    });
}

// --- Audit log ----------------------------------------------------

export interface AuditEvent {
  eventId: string;
  at: string;
  actorEmail: string;
  actorRole: 'CustomerAdmin' | 'Accountant' | 'Approver' | 'Viewer' | 'PlatformAdmin';
  action: string;
  resourceType: 'invoice' | 'tenant' | 'user' | 'erp_config' | 'api_key' | 'tdd' | 'archive';
  resourceId: string;
  ipAddress: string;
  outcome: 'success' | 'failure';
  detail?: string;
}

const AUDIT_ACTIONS: { action: string; resourceType: AuditEvent['resourceType'] }[] = [
  { action: 'invoice.create',         resourceType: 'invoice' },
  { action: 'invoice.submit',         resourceType: 'invoice' },
  { action: 'invoice.cancel',         resourceType: 'invoice' },
  { action: 'invoice.approve',        resourceType: 'invoice' },
  { action: 'invoice.reject',         resourceType: 'invoice' },
  { action: 'archive.search',         resourceType: 'archive' },
  { action: 'archive.export.pdf',     resourceType: 'archive' },
  { action: 'archive.export.csv',     resourceType: 'archive' },
  { action: 'user.invite',            resourceType: 'user' },
  { action: 'user.revoke',            resourceType: 'user' },
  { action: 'mfa.update',             resourceType: 'user' },
  { action: 'erp.config.update',      resourceType: 'erp_config' },
  { action: 'api_key.create',         resourceType: 'api_key' },
  { action: 'api_key.revoke',         resourceType: 'api_key' },
  { action: 'tdd.resubmit',           resourceType: 'tdd' },
  { action: 'tenant.profile.update',  resourceType: 'tenant' },
];

export function generateAuditEvents(count = 30, seed = 0xa0_d_1): AuditEvent[] {
  const rng = createRng(seed);
  const events: AuditEvent[] = [];
  for (let i = 0; i < count; i++) {
    const a = rng.pick(AUDIT_ACTIONS);
    const ago = rng.int(0, 60); // days
    const at = new Date();
    at.setUTCDate(at.getUTCDate() - ago);
    at.setUTCHours(rng.int(7, 22), rng.int(0, 59), rng.int(0, 59), 0);
    events.push({
      eventId: pseudoUuid(rng),
      at: at.toISOString(),
      actorEmail: rng.pick([
        'aisha@dubaitrading.ae',
        'omar.khalifa@dubaitrading.ae',
        'finance@dubaitrading.ae',
        'mariam.maktoum@dubaitrading.ae',
        'platform-admin@solara.ae',
      ]),
      actorRole: rng.pick(['CustomerAdmin', 'Accountant', 'Approver', 'Viewer', 'PlatformAdmin']),
      action: a.action,
      resourceType: a.resourceType,
      resourceId: pseudoUuid(rng),
      ipAddress: `${rng.int(80, 220)}.${rng.int(0, 255)}.${rng.int(0, 255)}.${rng.int(0, 255)}`,
      outcome: rng.chance(0.93) ? 'success' : 'failure',
      detail: rng.chance(0.4) ? `Initiated from ${rng.pick(['portal', 'api', 'erp_sap'])}` : undefined,
    });
  }
  events.sort((a, b) => (a.at < b.at ? 1 : -1));
  return events;
}

// --- Notifications ------------------------------------------------

export type NotificationKind =
  | 'invoice.delivered'
  | 'invoice.rejected'
  | 'tdd.deadline'
  | 'tdd.accepted'
  | 'mfa.required'
  | 'cert.expiry'
  | 'system.update';

export interface SampleNotification {
  notificationId: string;
  kind: NotificationKind;
  /** English fallback title (also used for telemetry / search). */
  title: string;
  /** English fallback body. */
  body: string;
  /** Variable bag the consumer interpolates into the localised message. */
  vars: Record<string, string | number>;
  at: string;
  read: boolean;
  href: string;
}

export function generateNotifications(count = 22, seed = 0xb0a1_47): SampleNotification[] {
  const rng = createRng(seed);
  const out: SampleNotification[] = [];
  const samples: {
    kind: NotificationKind;
    title: string;
    bodyFn: (vars: Record<string, string | number>) => string;
    varsFn: (rng: Rng) => Record<string, string | number>;
    href: string;
  }[] = [
    {
      kind: 'invoice.delivered',
      title: 'Invoice delivered',
      bodyFn: (v) => `${v.number} accepted by recipient ASP.`,
      varsFn: (rng) => ({
        number: `INV-2026-04-${String(rng.int(1, 9999)).padStart(4, '0')}`,
      }),
      href: '/invoices',
    },
    {
      kind: 'invoice.rejected',
      title: 'Invoice rejected',
      bodyFn: (v) => `${v.number} failed Schematron (BR-AE-VAT-08).`,
      varsFn: (rng) => ({
        number: `INV-2026-04-${String(rng.int(1, 9999)).padStart(4, '0')}`,
      }),
      href: '/invoices',
    },
    {
      kind: 'tdd.deadline',
      title: 'TDD deadline soon',
      bodyFn: (v) => `${v.count} TDD submissions due in the next 48 hours.`,
      varsFn: (rng) => ({ count: rng.int(1, 12) }),
      href: '/compliance',
    },
    {
      kind: 'tdd.accepted',
      title: 'EmaraTax confirmed',
      bodyFn: (v) => `${v.count} TDD documents acknowledged by EmaraTax.`,
      varsFn: (rng) => ({ count: rng.int(2, 80) }),
      href: '/compliance',
    },
    {
      kind: 'mfa.required',
      title: 'Re-confirm MFA',
      bodyFn: () => 'Two-factor verification expires in 7 days.',
      varsFn: () => ({}),
      href: '/settings/security',
    },
    {
      kind: 'cert.expiry',
      title: 'Certificate alert',
      bodyFn: () => 'mTLS certificate expires in 60 days.',
      varsFn: () => ({}),
      href: '/settings/security',
    },
    {
      kind: 'system.update',
      title: 'Maintenance window',
      bodyFn: () => 'Scheduled maintenance: Sunday 02:00–04:00 GST.',
      varsFn: () => ({}),
      href: '/',
    },
  ];
  for (let i = 0; i < count; i++) {
    const s = rng.pick(samples);
    const ago = rng.int(0, 30);
    const at = new Date();
    at.setUTCDate(at.getUTCDate() - ago);
    at.setUTCHours(rng.int(7, 22), rng.int(0, 59));
    const vars = s.varsFn(rng);
    out.push({
      notificationId: pseudoUuid(rng),
      kind: s.kind,
      title: s.title,
      body: s.bodyFn(vars),
      vars,
      at: at.toISOString(),
      read: rng.chance(0.6),
      href: s.href,
    });
  }
  out.sort((a, b) => (a.at < b.at ? 1 : -1));
  return out;
}

// --- Templates ----------------------------------------------------

export interface InvoiceTemplate {
  templateId: string;
  name: string;
  description: string;
  buyerName: string;
  buyerTrn: string;
  vatRate: 5 | 0 | 'EXEMPT';
  itemDescription: string;
  unitPrice: number;
  category: string;
}

export function generateInvoiceTemplates(seed = 0x7e_b1): InvoiceTemplate[] {
  const rng = createRng(seed);
  const samples = [
    { name: 'Monthly retainer — Office lease',     desc: 'Standard monthly office lease invoice.', cat: 'Real estate',   item: 'Office lease, Gate Avenue Tower 2', unit: 18500 },
    { name: 'IT consulting — Day rate',             desc: 'Senior consultant day-rate invoice.',     cat: 'Tech',          item: 'Senior consultant — 8h on-site',     unit: 2400  },
    { name: 'Cloud infra — AWS resale',             desc: 'Resale of AWS reserved capacity.',        cat: 'Tech',          item: 'AWS reserved instance — m5.large',   unit: 165   },
    { name: 'Logistics — Container 40HC',           desc: 'Sea-freight container 40\' high-cube.',   cat: 'Logistics',     item: '40HC container Jebel Ali → Port Klang', unit: 3850 },
    { name: 'Catering — In-flight',                 desc: 'Per-pax catering invoice.',               cat: 'Aviation',      item: 'In-flight meal box, regional',       unit: 84    },
    { name: 'Healthcare — Diagnostics package',     desc: 'Standard diagnostics block invoice.',     cat: 'Healthcare',    item: 'Full body MRI + report',             unit: 1850  },
  ];
  return samples.map((s) => ({
    templateId: 'tmpl-' + rng.int(100000, 999999),
    name: s.name,
    description: s.desc,
    buyerName: rng.pick(['Emaar Properties PJSC', 'DP World FZE', 'Etisalat by e&', 'Aldar Properties', 'Mubadala Investment']),
    buyerTrn: '1' + Array.from({ length: 14 }, () => rng.int(0, 9)).join(''),
    vatRate: 5,
    itemDescription: s.item,
    unitPrice: s.unit,
    category: s.cat,
  }));
}

// --- Inbound (C3) -------------------------------------------------

export interface InboundInvoice {
  invoiceId: string;
  invoiceNumber: string;
  receivedAt: string;
  senderName: string;
  senderNameAr: string;
  senderTrn: string;
  totalGrossAmount: number;
  currency: 'AED';
  /** Has the receiver-side ERP been notified successfully? */
  erpWriteback: 'pending' | 'success' | 'failed';
  /** Approval / payment state on the receiver side. */
  state: 'received' | 'reviewed' | 'paid' | 'disputed';
  attachmentCount: number;
}

export function generateInboundInvoices(count = 22, seed = 0x1dec_a1): InboundInvoice[] {
  const rng = createRng(seed);
  const out: InboundInvoice[] = [];
  for (let i = 0; i < count; i++) {
    const sender: SampleCompanyShim = SHIM_COMPANIES[i % SHIM_COMPANIES.length] as SampleCompanyShim;
    const receivedAt = new Date();
    receivedAt.setUTCDate(receivedAt.getUTCDate() - rng.int(0, 60));
    receivedAt.setUTCHours(rng.int(7, 21), rng.int(0, 59));
    const total = Math.round(rng.next() * 80_000 + 500);
    out.push({
      invoiceId: pseudoUuid(rng),
      invoiceNumber: `IN-${receivedAt.getUTCFullYear()}-${String(receivedAt.getUTCMonth() + 1).padStart(2, '0')}-${String(rng.int(1, 9999)).padStart(4, '0')}`,
      receivedAt: receivedAt.toISOString(),
      senderName: sender.nameEn,
      senderNameAr: sender.nameAr,
      senderTrn: '1' + Array.from({ length: 14 }, () => rng.int(0, 9)).join(''),
      totalGrossAmount: total,
      currency: 'AED',
      erpWriteback: rng.chance(0.7) ? 'success' : rng.chance(0.5) ? 'pending' : 'failed',
      state: rng.pick(['received', 'reviewed', 'paid', 'disputed']),
      attachmentCount: rng.int(0, 3),
    });
  }
  out.sort((a, b) => (a.receivedAt < b.receivedAt ? 1 : -1));
  return out;
}

// Shim to avoid circular dep with uae.ts COMPANIES (kept private here)
type SampleCompanyShim = { nameEn: string; nameAr: string };
const SHIM_COMPANIES: SampleCompanyShim[] = [
  { nameEn: 'Emaar Properties PJSC',     nameAr: 'إعمار العقارية' },
  { nameEn: 'DP World FZE',              nameAr: 'موانئ دبي العالمية' },
  { nameEn: 'Etisalat by e&',            nameAr: 'اتصالات' },
  { nameEn: 'Aldar Properties',          nameAr: 'الدار العقارية' },
  { nameEn: 'Mubadala Investment Co.',   nameAr: 'مبادلة للاستثمار' },
  { nameEn: 'ADNOC Distribution',        nameAr: 'أدنوك للتوزيع' },
  { nameEn: 'Majid Al Futtaim',          nameAr: 'ماجد الفطيم' },
  { nameEn: 'Damac Properties',          nameAr: 'داماك العقارية' },
  { nameEn: 'Dubai Holding',             nameAr: 'دبي القابضة' },
  { nameEn: 'IHC',                       nameAr: 'IHC الإماراتية القابضة' },
  { nameEn: 'EGA — Emirates Global Aluminium', nameAr: 'الإمارات العالمية للألمنيوم' },
  { nameEn: 'Etihad Airways',            nameAr: 'الاتحاد للطيران' },
  { nameEn: 'flydubai',                  nameAr: 'فلاي دبي' },
  { nameEn: 'Dnata',                     nameAr: 'دناتا' },
  { nameEn: 'Jumeirah Group',            nameAr: 'مجموعة جميرا' },
  { nameEn: 'Rove Hotels',               nameAr: 'فنادق روڤ' },
  { nameEn: 'Emirates NBD',              nameAr: 'بنك الإمارات دبي الوطني' },
  { nameEn: 'First Abu Dhabi Bank',      nameAr: 'بنك أبوظبي الأول' },
  { nameEn: 'Mashreq Bank',              nameAr: 'بنك المشرق' },
  { nameEn: 'ADIB — Abu Dhabi Islamic',  nameAr: 'مصرف أبوظبي الإسلامي' },
  { nameEn: 'Aramex',                    nameAr: 'أرامكس' },
  { nameEn: 'Talabat',                   nameAr: 'طلبات' },
];

// --- Approval queue ----------------------------------------------

export interface ApprovalRequest {
  approvalId: string;
  invoiceNumber: string;
  buyerName: string;
  buyerNameAr: string;
  totalGrossAmount: number;
  submittedAt: string;
  submittedBy: string;
  state: 'pending' | 'approved' | 'rejected';
  reviewer?: string;
  reviewedAt?: string;
  comment?: string;
}

export function generateApprovalRequests(count = 22, seed = 0xa1aa1): ApprovalRequest[] {
  const rng = createRng(seed);
  const out: ApprovalRequest[] = [];
  const submitters = ['aisha@dubaitrading.ae', 'omar.khalifa@dubaitrading.ae', 'finance@dubaitrading.ae'];
  const reviewers = ['mansour.sayegh@dubaitrading.ae', 'sara.tayer@dubaitrading.ae'];
  for (let i = 0; i < count; i++) {
    const sender = SHIM_COMPANIES[i % SHIM_COMPANIES.length] as SampleCompanyShim;
    const submittedAt = new Date();
    submittedAt.setUTCDate(submittedAt.getUTCDate() - rng.int(0, 14));
    submittedAt.setUTCHours(rng.int(7, 21), rng.int(0, 59));
    const state: ApprovalRequest['state'] =
      i < 8 ? 'pending' : rng.chance(0.7) ? 'approved' : 'rejected';
    out.push({
      approvalId: pseudoUuid(rng),
      invoiceNumber: `INV-2026-04-${String(rng.int(1, 9999)).padStart(4, '0')}`,
      buyerName: sender.nameEn,
      buyerNameAr: sender.nameAr,
      totalGrossAmount: rng.int(5_000, 250_000),
      submittedAt: submittedAt.toISOString(),
      submittedBy: rng.pick(submitters),
      state,
      ...(state !== 'pending'
        ? {
            reviewer: rng.pick(reviewers),
            reviewedAt: new Date(submittedAt.getTime() + rng.int(60_000, 86_400_000)).toISOString(),
            comment: state === 'rejected' ? 'Wrong cost-center allocation, please re-tag.' : undefined,
          }
        : {}),
    });
  }
  // Pending first.
  out.sort((a, b) => {
    if (a.state === 'pending' && b.state !== 'pending') return -1;
    if (b.state === 'pending' && a.state !== 'pending') return 1;
    return a.submittedAt < b.submittedAt ? 1 : -1;
  });
  return out;
}

// --- ERP integration health -------------------------------------

export interface ErpConnectorStatus {
  vendor: 'SAP' | 'Oracle' | 'Dynamics365' | 'Zoho' | 'NetSuite' | 'Tally';
  enabled: boolean;
  endpoint: string;
  username: string;
  lastSyncAt: string | null;
  recordsLast30d: number;
  errorRate: number;
  status: 'healthy' | 'degraded' | 'failing';
}

export function generateErpConnectors(seed = 0xe_a1): ErpConnectorStatus[] {
  const rng = createRng(seed);
  const all: ErpConnectorStatus['vendor'][] = ['SAP', 'Oracle', 'Dynamics365', 'Zoho', 'NetSuite', 'Tally'];
  return all.map((vendor) => {
    const enabled = rng.chance(0.6);
    return {
      vendor,
      enabled,
      endpoint: `https://${vendor.toLowerCase()}.dubaitrading.ae/api`,
      username: `solara-svc@${vendor.toLowerCase()}`,
      lastSyncAt: enabled
        ? new Date(Date.now() - rng.int(60_000, 6 * 3600_000)).toISOString()
        : null,
      recordsLast30d: enabled ? rng.int(20, 4500) : 0,
      errorRate: enabled ? Number((rng.next() * 0.05).toFixed(4)) : 0,
      status: !enabled ? 'healthy' : rng.chance(0.78) ? 'healthy' : rng.chance(0.6) ? 'degraded' : 'failing',
    };
  });
}

// --- API keys -----------------------------------------------------

export interface ApiKey {
  keyId: string;
  label: string;
  prefix: string; /** last 8 chars displayed, full key shown only on creation. */
  scopes: string[];
  createdAt: string;
  createdBy: string;
  lastUsedAt: string | null;
  status: 'active' | 'revoked';
}

export function generateApiKeys(seed = 0xa_1_e_4): ApiKey[] {
  const rng = createRng(seed);
  const labels = [
    'Production · SAP connector',
    'Staging · QA suite',
    'CI/CD · GitHub Actions',
    'Monitoring · Datadog probe',
    'Backup ETL job',
    'Power BI report ingestion',
  ];
  return labels.map((label, i) => {
    const status: ApiKey['status'] = i === labels.length - 1 ? 'revoked' : 'active';
    return {
      keyId: pseudoUuid(rng),
      label,
      prefix: 'solara_' + Array.from({ length: 4 }, () => rng.int(0, 15).toString(16)).join('') + '…',
      scopes: rng.pick([
        ['invoices:read', 'invoices:write'],
        ['invoices:read'],
        ['invoices:read', 'archive:read'],
        ['invoices:write', 'webhooks:subscribe'],
      ]),
      createdAt: new Date(Date.now() - rng.int(86_400_000, 200 * 86_400_000)).toISOString(),
      createdBy: 'aisha@dubaitrading.ae',
      lastUsedAt:
        status === 'active'
          ? new Date(Date.now() - rng.int(60_000, 5 * 86_400_000)).toISOString()
          : null,
      status,
    };
  });
}
